import React, {useEffect, useState} from 'react'
import {DataGrid, GridColDef} from '@mui/x-data-grid'
import {Box} from '@mui/material'
import {useAuth} from '../../contexts/auth.context'
import Loading from '../../components/loading.component'
import DateFormat from '../../components/date-format.component'
import {
  downloadDocument,
  getSearcherCompany,
  getUploadedFileDetails,
} from '../../services/order.service'

type Props = {
 searchCompanyId : number; 
}
const DocumentsAndCompliance: React.FC<Props> = ({ searchCompanyId }) => {
  const [fileDetails, setFileDetails]: any[] = useState([])
  const [loading, setLoading]: any[] = useState(false)

  const columns: GridColDef[] = [
    {
      field: 'fileS3Path',
      headerName: '',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <div
          style={{width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}
        >
          <button
            type='button'
            aria-label={`Download ${params.row.fileName}`}
            onClick={() => downloadDocumentFile(params.row.fileName)}
            style={{
              position: 'relative',
              width: 40,
              height: 40,
              padding: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            <img
              src='/media/ATR/icons/PDF-icon.png'
              alt='PDF'
              style={{width: 40, height: 40, display: 'block'}}
            />
          </button>
        </div>
      ),
    },
    {
      field: 'complianceName',
      headerName: 'File Type',
      sortable: false,
      width: 240,
    },
    {
      field: 'fileName',
      headerName: 'Document Name',
      width: 340,
      sortable: false,
      renderCell: (params) => (
        <a
          href='#'
          onClick={(e) => {
            e.preventDefault()
            downloadDocumentFile(params.row.fileName)
          }}
          style={{
            color: '#1a73e8',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          {params.value || 'N/A'}
        </a>
      ),
    },
    {
      field: 'fileStartDate',
      headerName: 'Create Date',
      sortable: false,
      width: 270,
      renderCell: (params) => {
        return (
          <div style={{display: 'flex', alignItems: 'center'}}>
            {params?.row?.fileStartDate ? <DateFormat date={params?.row?.fileStartDate} /> : 'N/A'}
          </div>
        )
      },
    },
    {
      field: 'fileExpirationDate',
      headerName: 'Expire Date',
      sortable: false,
      width: 270,
      renderCell: (params) => {
        return (
          <div style={{display: 'flex', alignItems: 'center'}}>
            {params?.row?.fileExpirationDate ? (
              <DateFormat date={params?.row?.fileExpirationDate} />
            ) : (
              'N/A'
            )}
          </div>
        )
      },
    },
    {
      field: 'Status',
      headerName: 'Status',
      width: 187,
      sortable: false,
      renderCell: (params) => {
        const statusMap = {
          green: 'Green Check.png',
          red: 'Red Check.png',
          orange: 'Orange Check.png',
        }

        const iconName =
          statusMap[params.row.status as keyof typeof statusMap] || 'Disable Check.png'

        return (
          <div
            style={{display: 'flex', alignItems: 'center', width: '100%', padding: '0 25px 0 15px'}}
          >
            <img
              src={`/media/ATR/icons/${iconName}`}
              alt='status'
              style={{width: 35, height: 35}}
            />
          </div>
        )
      },
    },
  ]


  useEffect(() => {
    if (searchCompanyId) {
      uploadedFileDetails()
    }
  }, [searchCompanyId])

  const uploadedFileDetails = () => {
    setLoading(true)
    if (searchCompanyId) {
      getUploadedFileDetails(Number(searchCompanyId))
        .then((data) => {
          if (data) {
            if (data?.error?.includes('file details not found')) {
              setFileDetails([])
            } else if (Array.isArray(data.files)) {
              const update = data.files.map((value: any) => {
                return {...value, success: 'true'}
              })
              setFileDetails(update)
            } else {
              setFileDetails([])
            }
          } else {
            setFileDetails([])
          }
        })
        .catch((error) => {
          setFileDetails([])
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const getRowId = (row: any) => row.fileS3Path

  const downloadDocumentFile = async (fileName: string) => {
    await downloadDocument(Number(searchCompanyId), fileName).then((data) => {
      if (data) {
        window.open(data?.presigned_url, '_blank')
      }
    })
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <Box sx={{position: 'relative'}}>
          <Box>
            <div
              style={{
                height: '44vh',
                minHeight: 350,
                width: '100%',
              }}
            >
              <DataGrid
                rows={fileDetails}
                columns={columns}
                hideFooter
                getRowId={getRowId}
                disableColumnMenu
                disableColumnSelector
                rowHeight={73}
                sx={{
                  border: '1px solid #ddd',

                  /* EACH CELL BORDER RIGHT */
                  '& .MuiDataGrid-cell': {
                    borderRight: '1px solid #e0e0e0',
                    borderBottom: '1px solid #f0f0f0',
                    padding: '12px',
                    fontSize: '16px',
                    outline: 'none !important',
                  },

                  /* REMOVE LAST CELL RIGHT BORDER */
                  '& .MuiDataGrid-row .MuiDataGrid-cell:last-of-type': {
                    borderRight: 'none',
                  },

                  /* COLUMN HEADER BORDER RIGHT */
                  '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
                    borderRight: '1px solid #e0e0e0',
                  },

                  /* REMOVE LAST HEADER RIGHT BORDER */
                  '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:last-of-type': {
                    borderRight: 'none',
                  },

                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f6f6f6',
                    borderBottom: '1px solid #ddd',
                    height: '60px',
                  },

                  '& .MuiDataGrid-columnSeparator': {
                    display: 'none',
                  },

                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#000',
                  },
                }}
              />
            </div>
          </Box>
        </Box>
      )}
    </>
  )
}

export {DocumentsAndCompliance}
